import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  Skeleton } from '@mui/material';
import { ChargeSetting } from 'graphql/charge-settings/queries';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { MobileSettingCard } from './components/mobile-setting-card';
import { Row } from './components/row';
import { useStyles } from './styles';
import { columns } from './utils/index';

const SKELETON_ROWS = Array.from({ length: 10 }, (_, i) => `skeleton-row-${i + 1}`);
const SKELETON_CELLS = Array.from({ length: 10 }, (_, i) => `skeleton-cell-${i + 1}`);
const SKELETON_CARDS = Array.from({ length: 3 }, (_, i) => `skeleton-card-${i + 1}`);

type TableWireTransfersProps = {
  settingsPages: Array<ChargeSetting[]>,
  currentPage: number,
  totalPages: number,
  rowsPerPage: number,
  totalRows: number,
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>,
  changeRowsPerPage: ({ rows }: { rows: number }) => void,
  isDataLoading: boolean,
  onDeleteSetting: (settingId: string) => void,
  onEditSetting: (setting: ChargeSetting) => void
}

export const SettingsTable: FC<TableWireTransfersProps> = ({
  settingsPages,
  currentPage,
  setCurrentPage,
  changeRowsPerPage,
  totalPages,
  rowsPerPage,
  totalRows,
  isDataLoading,
  onDeleteSetting,
  onEditSetting
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  // Cards through 1279px, dense desktop table from `lg` (1280px) up.
  // `noSsr` resolves the query synchronously so desktop never flashes cards on first render.
  const isCompact = !useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });

  const currentRows = totalPages > currentPage - 1 && settingsPages[currentPage - 1]
    ? settingsPages[currentPage - 1]
    : [];
  const isEmpty = !isDataLoading && currentRows.length === 0;

  const paginationProps = {
    rowsPerPageOptions: [5, 8, 10, 25],
    rowsPerPage,
    count: totalRows,
    page: currentPage - 1,
    onPageChange: (e: unknown, page: number) => { setCurrentPage(page + 1); },
    onRowsPerPageChange: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const value = Number(e.target.value);
      if (!Number.isNaN(value)) {
        changeRowsPerPage({ rows: value });
      }
    },
    labelRowsPerPage: t('common:rows-per-page'),
    SelectProps: {
      inputProps: { 'aria-label': t('common:rows-per-page'), },
      native: true,
    }
  };

  if (isCompact) {
    return (
      <>
        <div className={classes.list}>
          {isDataLoading && SKELETON_CARDS.map((cardKey) => (
            <Skeleton key={cardKey} variant="rectangular" className={classes.skeletonCard} />
          ))}
          {currentRows.map((setting) => (
            <MobileSettingCard
              key={setting._id}
              setting={setting}
              onDelete={onDeleteSetting}
              onEdit={onEditSetting}
            />
          ))}
          {isEmpty && (
          <Typography color="textSecondary" className={classes.empty}>
            {t('common:noChargeSettings')}
          </Typography>
          )}
        </div>
        <TablePagination
          component="div"
          {...paginationProps}
          classes={{
            root: classes.paginationRoot,
            toolbar: classes.paginationToolbar,
            spacer: classes.paginationSpacer,
            actions: classes.paginationActions
          }}
        />
      </>
    );
  }

  return (
    <TableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell
              align="center"
              colSpan={4}
              className={classes.headerCell}
            />
            <TableCell
              align="center"
              colSpan={2}
              className={classes.headerGroupCell}
            >
              {t('common:buyer')}
            </TableCell>
            <TableCell
              align="center"
              colSpan={2}
              className={classes.headerGroupCell}
            >
              {t('common:seller')}
            </TableCell>
          </TableRow>
          <TableRow>
            {columns(t).map((column) => (
              <TableCell
                className={classes.headerCell}
                key={column.id}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isDataLoading && (
            <>
              {SKELETON_ROWS.map((rowKey) => (
                <TableRow key={rowKey}>
                  {SKELETON_CELLS.map((cellKey) => (
                    <TableCell key={cellKey}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </>
          )}
          {currentRows.map((setting) => (
            <Row
              key={setting._id}
              setting={setting}
              onDelete={onDeleteSetting}
              onEdit={onEditSetting}
            />
          ))}
          {isEmpty && (
            <TableRow>
              <TableCell colSpan={10} align="center">
                <Typography color="textSecondary">
                  {t('common:noChargeSettings')}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination {...paginationProps} />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};
